// Descarga de fichas usando el endpoint del job-application-service que arma la
// ficha con datos del modelo LEGACY (public.persona) + documentos del modelo
// nuevo, identificando a las personas por RUT.
//
//   POST {APPLICANT}/application/download-ficha-legacy?userId=<id>
//   body: { candidates: [{ rut, busPerId?, busquedaId? }] }
//   → 1 candidato → application/pdf
//   → varios      → application/zip
//
// El proceso (busquedaId) que aparece en la ficha se resuelve por candidato:
// si la vista ya tiene el busquedaId lo manda directo; si no, manda el busPerId
// (PK de busquedaPersona) y el backend lo resuelve.
//
// Se usa desde las vistas legacy (Selección Masiva) con acción individual
// (un candidato) y masiva (varios), igual que la descarga de fichas de
// Postulaciones.

export interface IFichaLegacyCandidate {
  rut: string;
  busPerId?: string;
  busquedaId?: string;
}

// Un poco por encima del corte duro de 230 s del gateway de Azure Functions:
// si el backend no respondió para entonces, la request ya está perdida y no
// tiene sentido seguir esperando.
const REQUEST_TIMEOUT_MS = 240_000;

const triggerBlobDownload = (
  blob: Blob,
  fallbackName: string,
  contentDisposition: string | null
): void => {
  let filename = fallbackName;
  if (contentDisposition) {
    const match = /filename\*?=(?:UTF-8'')?["']?([^;"'\r\n]+)["']?/i.exec(
      contentDisposition
    );
    if (match?.[1]) filename = decodeURIComponent(match[1]);
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export const downloadFichaLegacyByRuts = async (
  candidates: IFichaLegacyCandidate[]
): Promise<void> => {
  const seen = new Set<string>();
  const cleanCandidates: IFichaLegacyCandidate[] = [];
  for (const candidate of candidates ?? []) {
    const rut = (candidate?.rut ?? "").trim();
    if (!rut || seen.has(rut)) continue;
    seen.add(rut);
    cleanCandidates.push({
      rut,
      busPerId: candidate?.busPerId,
      busquedaId: candidate?.busquedaId,
    });
  }
  if (cleanCandidates.length === 0) {
    throw new Error("No hay candidatos seleccionados para generar fichas.");
  }

  const userId = sessionStorage.getItem("usuarioId") ?? "";
  const baseUrl = process.env.REACT_APP_API_APPLICANT;
  if (!baseUrl) throw new Error("Configuración faltante: REACT_APP_API_APPLICANT.");

  // Sin timeout, si el servicio se cuelga el fetch queda pendiente para siempre
  // y la vista se queda con el botón en "cargando" hasta refrescar la página.
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  let res: Response;
  try {
    res = await fetch(
      `${baseUrl}/application/download-ficha-legacy?userId=${encodeURIComponent(userId)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ candidates: cleanCandidates }),
        signal: controller.signal,
      }
    );
  } catch (error) {
    if ((error as Error)?.name === "AbortError") {
      throw new Error(
        "La generación de fichas tardó demasiado. Intenta con menos postulantes."
      );
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }

  if (!res.ok) {
    if (res.status === 404) {
      throw new Error("No se encontraron personas para generar la(s) ficha(s).");
    }
    throw new Error(`HTTP ${res.status}`);
  }

  const blob = await res.blob();
  const fallbackName =
    cleanCandidates.length === 1
      ? `ficha_${cleanCandidates[0].rut}.pdf`
      : `fichas_${new Date().toISOString().slice(0, 10)}.zip`;
  triggerBlobDownload(blob, fallbackName, res.headers.get("Content-Disposition"));
};
